import { useEffect, useRef } from "react";
import {
  DefaultError,
  InfiniteData,
  QueryKey,
  useInfiniteQuery as useInfiniteQueryBase,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useQuery as useQueryBase,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

// TanStack Query v5 removed onSuccess/onError/onSettled from queries (they remain on mutations).
// Our screens pass these callbacks into the module query hooks, so these wrappers keep the
// v3 per-fetch semantics by watching dataUpdatedAt/errorUpdatedAt.
export type LegacyQueryCallbacks<TData = unknown, TError = unknown> = {
  onSuccess?: (data: TData) => void;
  onError?: (error: TError) => void;
  onSettled?: (data: TData | undefined, error: TError | null) => void;
};

function useLegacyCallbacks<TData, TError>(
  result: { data: TData | undefined; error: TError | null; dataUpdatedAt: number; errorUpdatedAt: number },
  callbacks: LegacyQueryCallbacks<TData, TError>,
) {
  const callbacksRef = useRef(callbacks);
  callbacksRef.current = callbacks;
  // Seed with the mount-time timestamps: v3 fired callbacks only for fetches, so an update that
  // is merely served from cache on mount must not fire them (e.g. it would re-toast an old error).
  const seenDataAt = useRef(result.dataUpdatedAt);
  const seenErrorAt = useRef(result.errorUpdatedAt);

  useEffect(() => {
    if (result.dataUpdatedAt && result.dataUpdatedAt !== seenDataAt.current) {
      seenDataAt.current = result.dataUpdatedAt;
      callbacksRef.current.onSuccess?.(result.data as TData);
      callbacksRef.current.onSettled?.(result.data, null);
    }
  }, [result.dataUpdatedAt, result.data]);

  useEffect(() => {
    if (result.errorUpdatedAt && result.errorUpdatedAt !== seenErrorAt.current) {
      seenErrorAt.current = result.errorUpdatedAt;
      if (result.error !== null) {
        callbacksRef.current.onError?.(result.error);
        callbacksRef.current.onSettled?.(undefined, result.error);
      }
    }
  }, [result.errorUpdatedAt, result.error]);
}

export function useQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey> & LegacyQueryCallbacks<TData, TError>,
): UseQueryResult<TData, TError> {
  const { onSuccess, onError, onSettled, ...queryOptions } = options;
  const result = useQueryBase(queryOptions);
  useLegacyCallbacks(result, { onSuccess, onError, onSettled });
  return result;
}

export function useInfiniteQuery<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  options: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam> & LegacyQueryCallbacks<TData, TError>,
): UseInfiniteQueryResult<TData, TError> {
  const { onSuccess, onError, onSettled, ...queryOptions } = options;
  const result = useInfiniteQueryBase(queryOptions);
  useLegacyCallbacks(result, { onSuccess, onError, onSettled });
  return result;
}
